/**
 * Normalize an axios error into a user-friendly French message.
 *
 * Distinguishes:
 *   - real network failure (no response, ERR_NETWORK, ECONNABORTED)
 *   - 4xx (validation, conflict, auth)
 *   - 5xx (backend crash — surface the structured `error`/`detail` field
 *     produced by our global exception handler in /app/backend/server.py)
 *
 * Returns `{ message, status, detail }`.
 */
export function describeApiError(err) {
  if (!err) return { message: 'Erreur inconnue', status: 0, detail: null };

  const status = err.response?.status;
  const data = err.response?.data;
  const rawDetail = data?.detail ?? data?.error ?? null;

  const detailText = Array.isArray(rawDetail)
    ? rawDetail.map((d) => d.msg || JSON.stringify(d)).join(' ; ')
    : (typeof rawDetail === 'string' ? rawDetail : rawDetail?.msg || null);

  if (!err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
    return {
      message: 'Le serveur est injoignable (réseau ou CORS). Vérifie ta connexion ; '
             + 'si le problème persiste, regarde les logs backend.',
      status: 0,
      detail: rawDetail,
    };
  }

  if (status === 401) {
    return { message: 'Session expirée — reconnecte-toi.', status, detail: rawDetail };
  }
  if (status === 403) {
    return { message: detailText || 'Accès refusé.', status, detail: rawDetail };
  }
  if (status === 404) {
    return { message: detailText || 'Ressource introuvable.', status, detail: rawDetail };
  }
  if (status === 409) {
    return { message: detailText || 'Conflit (déjà existant).', status, detail: rawDetail };
  }
  if (status === 422) {
    return {
      message: detailText || 'Données invalides — vérifie les champs.',
      status,
      detail: rawDetail,
    };
  }
  if (status >= 500) {
    return {
      message: `Erreur serveur (${status}) — ${detailText || 'voir la console pour la trace.'}`,
      status,
      detail: rawDetail,
    };
  }
  return {
    message: detailText || err.message || 'Erreur inconnue',
    status: status || 0,
    detail: rawDetail,
  };
}

export default describeApiError;
